import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getWorkloadAttributeValues } from "@/helpers/apis";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { WorkflowType } from "@/pages/query/types";

// Page of entity-name values for the EntityLegendPicker list.
export interface EntityValuesPage {
  values: string[];
  total: number;
  page: number;
  size: number;
}

interface UseEntityValuesArgs {
  groupBy: string;
  startDate: Date;
  endDate: Date;
  workflowType: WorkflowType;
  page: number;
  size: number;
  search?: string;
}

// Mirrors the namespace `SnowflakeJobs.tsx` already uses for the same
// endpoint (`["workload", "attribute_values", ...]`), so a future bulk
// `invalidateQueries({ queryKey: ["workload"] })` still catches both paths.
const QUERY_KEY_NAMESPACE = ["workload", "attribute_values"] as const;

// Paginated, prefix-searched fetch for the Snowflake Workloads
// attribute-values endpoint, wrapped in `useQuery` so the picker gets dedup,
// caching, and `keepPreviousData` smooth-pagination semantics without the
// consumer juggling fetch state itself.
export const useEntityValues = ({
  groupBy,
  startDate,
  endDate,
  workflowType,
  page,
  size,
  search,
}: UseEntityValuesArgs) => {
  return useQuery({
    queryKey: [
      ...QUERY_KEY_NAMESPACE,
      groupBy,
      startDate,
      endDate,
      workflowType,
      search,
      page,
      size,
    ],
    queryFn: async (): Promise<EntityValuesPage> => {
      const res = await getWorkloadAttributeValues({
        attribute_key: groupBy,
        start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
        end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString() || "",
        workflow_type: workflowType,
        page,
        size,
        search: search || undefined,
      });
      return {
        values: res?.values ?? [],
        total: res?.total ?? 0,
        page: res?.page ?? page,
        size: res?.size ?? size,
      };
    },
    placeholderData: keepPreviousData,
  });
};
